/**

* Authentication service

* Supports multiple auth providers (Local, Azure AD, Mock Azure AD)

*/

import { BaseService } from '@services/base.service';

import { LoginRequest, RegisterRequest, AuthResponse } from '../types/auth.types';

import { authConfig, AuthProvider } from '@core/config/auth.config';

import { azureADService } from './azure-ad.service';

import { mockAzureADService } from './mock-azure-ad.service';

import { mockAuthService } from './mock-auth.service';

import { axiosInstance } from '@core/config/axios.config';

import { ApiResponse } from '@core/types';
 
class AuthService extends BaseService<any> {

    constructor() {

        super('/auth');

    }
 
    /**

     * Get the appropriate Azure AD service (real or mock)

     */

    private getAzureADService() {

        return authConfig.useMockAzureAD ? mockAzureADService : azureADService;

    }
 
    /**

     * Check if we should use mock auth

     */

    private shouldUseMock(): boolean {

        return import.meta.env.DEV && window._env_.FTDQADC_USE_MOCK_AUTH === 'true';

    }
 
    /**

     * Initialize authentication

     */

    async initialize(): Promise<void> {

        if (authConfig.provider === AuthProvider.AZURE_AD) {

            const azureService = this.getAzureADService();

            await azureService.initialize();
 
            if (authConfig.useMockAzureAD) {

                console.log('✅ Mock Azure AD initialized (Development mode)');

            } else {

                console.log('✅ Real Azure AD initialized');

            }

        } else if (this.shouldUseMock()) {

            console.log('✅ Mock auth initialized (Development mode)');

        } else {

            console.log('✅ Local auth initialized');

        }

    }
 
    /**

     * Login - supports Azure AD (real/mock), Mock, and Real API

     */

    async login(credentials: LoginRequest): Promise<AuthResponse> {

        // Azure AD Login (real or mock) - uses SSO endpoint

        if (authConfig.provider === AuthProvider.AZURE_AD) {

            return this.loginWithAzureADSSO();

        }
 
        // Mock Login (Development)

        if (this.shouldUseMock()) {

            return mockAuthService.login(credentials);

        }
 
        // Real API Login

        const response = await axiosInstance.post(`${this.endpoint}/login`, credentials);

        return response.data.data;

    }
 
    /**

     * Azure AD SSO Login

     * 1. Authenticate with Azure AD (MSAL)

     * 2. Get ID token from Azure AD

     * 3. Send ID token to backend SSO endpoint

     * 4. Backend validates and returns app tokens

     */

    async loginWithAzureADSSO(): Promise<AuthResponse> {

        const azureService = this.getAzureADService();
 
        // Step 1: Authenticate with Azure AD

        await azureService.loginWithPopup();
 
        // Step 2: Get ID token from Azure AD

        const idToken = await azureService.getIdTokenAsync();

        if (!idToken) {

            throw new Error('Failed to obtain ID token from Azure AD');

        }
 
        // Step 3 & 4: Send ID token to backend SSO endpoint
        // Ensure we're using the correct baseURL
        const ssoUrl = `${this.endpoint}/sso/azure-ad`;
        
        // Log in development to help debug
        if (import.meta.env.DEV) {
            const baseURL = axiosInstance.defaults.baseURL;
            console.log('🔧 SSO Login URL:', baseURL ? `${baseURL}${ssoUrl}` : ssoUrl);
        }

        const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
            ssoUrl,
            { id_token: idToken },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        const data = response.data as any;

        if (!response?.data) {

            console.error('❌ Invalid SSO response structure. Full response:', {

                responseData: response.data,

                responseStatus: response.status,

                responseHeaders: response.headers,

            });

            throw new Error('❌ Invalid SSO response structure. Full response:');

        }

        const authData: AuthResponse = {

            user: { ...data.user, name: data?.user?.first_name + ' ' + data?.user.last_name, roles: [data?.user?.role] },

            accessToken: data.accessToken || data.access_token,

            refreshToken: data.refreshToken || data.refresh_token || '',

        };

        return authData;

    }
 
 
    /**

     * Register - only for local/mock auth

     */

    async register(data: RegisterRequest): Promise<AuthResponse> {

        if (authConfig.provider === AuthProvider.AZURE_AD) {

            throw new Error('Registration not supported with Azure AD');

        }
 
        // Mock Register (Development)

        if (this.shouldUseMock()) {

            return mockAuthService.register(data);

        }
 
        // Real API Register

        const response = await axiosInstance.post(`${this.endpoint}/register`, data);

        return response.data.data;

    }
 
    /**

     * Logout

     */

    async logout(): Promise<void> {

        if (authConfig.provider === AuthProvider.AZURE_AD) {

            const azureService = this.getAzureADService();

            await azureService.logout();

            return;

        }
 
        if (this.shouldUseMock()) {

            await mockAuthService.logout();

            return;

        }
 
        // Real API Logout

        await axiosInstance.post(`${this.endpoint}/logout`);

    }
 
    /**

     * Check if user is authenticated

     */

    isAuthenticated(): boolean {

        if (authConfig.provider === AuthProvider.AZURE_AD) {

            const azureService = this.getAzureADService();

            return azureService.isAuthenticated();

        }

        return false;

    }
 
    /**

     * Get current user

     */

    async getCurrentUser(): Promise<any> {

        if (authConfig.provider === AuthProvider.AZURE_AD) {

            const azureService = this.getAzureADService();

            return azureService.getUserInfo();

        }

        if (this.shouldUseMock()) {

            return mockAuthService.getCurrentUser();

        }

        const response = await axiosInstance.get(`${this.endpoint}/me`);

        return response.data.data;

    }

    /**
     * Handle redirect callback after Azure AD authentication
     * Similar to loginWithAzureADSSO but for redirect flow
     */
    async handleRedirectCallback(): Promise<AuthResponse> {
        if (authConfig.provider !== AuthProvider.AZURE_AD) {
            throw new Error('Redirect callback only supported for Azure AD');
        }

        const azureService = this.getAzureADService();

        // Handle the redirect promise
        await azureService.handleRedirectCallback();

        // Get ID token from Azure AD
        const idToken = await azureService.getIdTokenAsync();

        if (!idToken) {
            throw new Error('Failed to obtain ID token from Azure AD');
        }

        // Send ID token to backend SSO endpoint
        const ssoUrl = `${this.endpoint}/sso/azure-ad`;
        
        if (import.meta.env.DEV) {
            const baseURL = axiosInstance.defaults.baseURL;
            console.log('🔧 SSO Login URL:', baseURL ? `${baseURL}${ssoUrl}` : ssoUrl);
        }

        const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
            ssoUrl,
            { id_token: idToken },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        const data = response.data as any;

        if (!response?.data) {
            console.error('❌ Invalid SSO response structure. Full response:', {
                responseData: response.data,
                responseStatus: response.status,
                responseHeaders: response.headers,
            });
            throw new Error('❌ Invalid SSO response structure. Full response:');
        }

        const authData: AuthResponse = {
            user: { ...data.user, name: data?.user?.first_name + ' ' + data?.user.last_name, roles: [data?.user?.role] },
            accessToken: data.accessToken || data.access_token,
            refreshToken: data.refreshToken || data.refresh_token || '',
        };
        
        return authData;
    }

}
 
export const authService = new AuthService();

 